import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { format, parseISO, isValid, differenceInDays } from "date-fns"
import { tr } from "date-fns/locale"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatPrice(
  price: number,
  options: {
    currency?: string
    notation?: Intl.NumberFormatOptions['notation']
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  } = {}
) {
  const {
    currency = 'USD',
    notation = 'standard',
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    notation,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(price)
}

/**
 * Bir tarih string'ini formatlar ve Türkçe olarak görüntüler
 * @param dateString ISO formatında tarih string'i
 * @returns Formatlanmış tarih (örn: '15 Mart 2023')
 */
export function formatDate(dateString: string): string {
  if (!dateString) return '';

  const date = new Date(dateString);

  // Geçersiz tarih kontrolü
  if (isNaN(date.getTime())) return '';

  // Türkçe ay isimleri
  const months = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ];

  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
}

export function calculateDuration(startDate: string | null | undefined, endDate: string | null | undefined): string {
  if (!startDate || !endDate) return '-'

  try {
    const start = parseISO(startDate)
    const end = parseISO(endDate)

    if (!isValid(start) || !isValid(end)) return '-'

    const days = differenceInDays(end, start)
    return `${days} Gün`
  } catch (error) {
    console.error('Duration calculation error:', error)
    return '-'
  }
}

export function formatDuration(days: number): string {
  return `${days} Gün`
}

export function getMonthName(month: number): string {
  return new Intl.DateTimeFormat('tr-TR', { month: 'long' }).format(new Date(2024, month - 1))
}

export function generateYearMonths(startMonth: number = new Date().getMonth() + 1): Array<{ value: string, label: string }> {
  const months = []
  const currentYear = new Date().getFullYear()

  for (let i = 0; i < 12; i++) {
    const monthIndex = (startMonth + i - 1) % 12
    const year = currentYear + Math.floor((startMonth + i - 1) / 12)
    const month = monthIndex + 1

    months.push({
      value: `${month}`,
      label: `${getMonthName(month)} ${year}`
    })
  }

  return months
}

export function calculateTotalPrice(pricePerPerson: number, numberOfPeople: number): number {
  return pricePerPerson * numberOfPeople
}

export function formatTourType(type: string): string {
  const types = {
    'UMRE': 'Umre',
    'HAC': 'Hac',
    'RAMAZAN': 'Ramazan Umresi',
    'STANDARD': 'Standart',
    'LUXURY': 'Lüks'
  }
  return types[type as keyof typeof types] || type
}

export function getRoomTypeLabel(type: number): string {
  return `${type} Kişilik Oda`;
}

export const getCsrfToken = (): string => {
  if (typeof window === 'undefined') return ''

  const metaTag = document.querySelector('meta[name="csrf-token"]')
  return metaTag?.getAttribute('content') || ''
}

const rateLimits: Record<string, { count: number; timestamp: number }> = {};

export const checkRateLimit = (action: string, limit = 5, window = 60000): boolean => {
  const now = Date.now();
  const actionData = rateLimits[action] || { count: 0, timestamp: now };

  if (now - actionData.timestamp > window) {
    rateLimits[action] = { count: 1, timestamp: now };
    return true;
  }

  if (actionData.count >= limit) {
    return false;
  }

  rateLimits[action] = {
    count: actionData.count + 1,
    timestamp: actionData.timestamp
  };

  return true;
};

export function formatDateRange(startDate: string | Date, endDate: string | Date) {
  const start = new Date(startDate)
  const end = new Date(endDate)

  if (start.getFullYear() === end.getFullYear()) {
    if (start.getMonth() === end.getMonth()) {
      return `${start.getDate()}-${end.getDate()} ${start.toLocaleString('default', { month: 'long' })} ${start.getFullYear()}`
    }
    return `${start.getDate()} ${start.toLocaleString('default', { month: 'short' })} - ${end.getDate()} ${end.toLocaleString('default', { month: 'long' })} ${start.getFullYear()}`
  }

  return `${formatDate(start.toISOString())} - ${formatDate(end.toISOString())}`
}

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};
